// vim: set ts=2 sts=2 sw=2 et:

import React from "react";

interface Props {
  children: string;
  maxFontSize: number;
  minFontSize: number;
  /** Líneas permitidas. Por defecto, una. */
  maxLines?: number;
  /** Con `maxLines` > 1: hasta dónde se achica en una línea antes de pasar a varias (por defecto, 75% del máximo). */
  singleLineMinFontSize?: number;
  /** Con `maxLines` > 1: tamaño inicial al repartir en varias líneas, para no pasarse del alto disponible. */
  wrapMaxFontSize?: number;
  className?: string;
}

const overflows = (element: HTMLElement, fontSize: number, maxLines: number): boolean => {
  if (element.scrollWidth > element.clientWidth) return true;
  if (maxLines === 1) return false;
  // Se cuentan líneas redondeando: tildes y descendentes de Anton suman unos píxeles al alto de desplazamiento.
  const lineHeight = parseFloat(window.getComputedStyle(element).lineHeight) || fontSize * 1.15;
  return Math.round(element.scrollHeight / lineHeight) > maxLines;
};

const shrinkToFit = (element: HTMLElement, from: number, to: number, maxLines: number): boolean => {
  let fontSize = from;
  element.style.fontSize = `${fontSize}px`;
  while (fontSize > to && overflows(element, fontSize, maxLines)) {
    fontSize -= 1;
    element.style.fontSize = `${fontSize}px`;
  }
  return !overflows(element, fontSize, maxLines);
};

const resetClamp = (element: HTMLElement) => {
  element.style.display = "";
  element.style.webkitLineClamp = "";
  element.style.webkitBoxOrient = "";
  element.style.textOverflow = "";
};

// Achica la fuente hasta que el texto entra. Con `maxLines` > 1 primero intenta una sola línea y recién después
// reparte en más: un nombre corto no se parte por unos píxeles, y uno larguísimo baja a dos líneas legibles.
// Si ni así entra, corta con "…" en lugar de recortar letras.
// Mide al cambiar el texto: los lienzos son de ancho fijo (1920px escalados con transform). Si el ancho cambia,
// quien lo usa lo remonta con otra `key`.
const FitLine = ({
  children,
  maxFontSize,
  minFontSize,
  maxLines = 1,
  singleLineMinFontSize,
  wrapMaxFontSize,
  className,
}: Props) => {
  const ref = React.useRef<HTMLDivElement>(null);

  React.useLayoutEffect(() => {
    const element = ref.current;
    if (element === null) return;
    resetClamp(element);
    element.style.whiteSpace = "nowrap";
    element.dataset.fit = "single";
    if (maxLines > 1) {
      const singleLineMin = singleLineMinFontSize ?? Math.max(minFontSize, Math.round(maxFontSize * 0.75));
      if (shrinkToFit(element, maxFontSize, singleLineMin, 1)) return;
      element.style.whiteSpace = "normal";
      element.dataset.fit = "wrapped";
    }
    if (shrinkToFit(element, wrapMaxFontSize ?? maxFontSize, minFontSize, maxLines)) return;
    element.dataset.fit = "clamped";
    if (maxLines === 1) {
      element.style.textOverflow = "ellipsis";
    } else {
      element.style.display = "-webkit-box";
      element.style.webkitLineClamp = String(maxLines);
      element.style.webkitBoxOrient = "vertical";
    }
  }, [children, maxFontSize, maxLines, minFontSize, singleLineMinFontSize, wrapMaxFontSize]);

  return (
    <div
      ref={ref}
      className={className}
      style={{ overflow: "hidden", overflowWrap: "anywhere", whiteSpace: "nowrap", width: "100%" }}
    >
      {children}
    </div>
  );
};

export default FitLine;
